import { FC } from 'react';
import { Content, ImageField, isFilled } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import DynamicMarquee from '@/components/DynamicMarquee';
import { RevealOnScroll } from '@/components/common/RevealOnScroll';
import { LabeledGroup } from './components/LabeledGroup';
import { LogoCard } from './components/LogoCard';
import { RecognitionStrip } from './components/RecognitionStrip';
import { SectionIntro } from './components/SectionIntro';

export type PartnersCertificationsRecognitionsProps =
  SliceComponentProps<Content.PartnersCertificationsRecognitionsSlice>;

type LogoItem = { logo: ImageField };

const filledLogos = (items: LogoItem[] | null | undefined) =>
  (items ?? []).filter((item) => isFilled.image(item.logo));

const PartnersCertificationsRecognitions: FC<
  PartnersCertificationsRecognitionsProps
> = ({ slice }) => {
  const {
    title,
    description,
    partners_label,
    certifications_label,
    recognitions_label,
    partners,
    certifications,
    recognitions,
  } = slice.primary;

  const partnerLogos = filledLogos(partners);
  const certificationLogos = filledLogos(certifications);
  // Keep the full recognition item shape (not just `logo`) so the optional
  // `logo_color` hover asset stays accessible.
  const recognitionLogos = (recognitions ?? []).filter((item) =>
    isFilled.image(item.logo),
  );

  const hasContent =
    isFilled.richText(title) ||
    isFilled.richText(description) ||
    partnerLogos.length > 0 ||
    certificationLogos.length > 0 ||
    recognitionLogos.length > 0;

  if (!hasContent) {
    return null;
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white px-4 py-[60px] font-inter md:px-[30px] md:py-[80px]"
    >
      <RevealOnScroll className="relative z-[1] mx-auto flex w-full max-w-[1280px] flex-col gap-10 md:gap-12">
        <SectionIntro
          title={
            isFilled.richText(title) ? (
              <PrismicRichText
                field={title}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="w-full break-words text-[28px] font-semibold leading-[1.2] text-[#041118] md:text-[42px]">
                      {children}
                    </h2>
                  ),
                }}
              />
            ) : undefined
          }
          description={
            isFilled.richText(description) ? (
              <div className="w-full break-words text-[16px] font-light leading-[1.5] text-[#041118] md:text-[18px] [&_p]:m-0 [&_p+p]:mt-3">
                <PrismicRichText
                  field={description}
                  components={{
                    paragraph: ({ children }) => <p>{children}</p>,
                    strong: ({ children }) => (
                      <strong className="font-semibold">{children}</strong>
                    ),
                  }}
                />
              </div>
            ) : undefined
          }
        />

        {partnerLogos.length > 0 && (
          <LabeledGroup label={partners_label}>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
              {partnerLogos.map((item, index) => (
                <LogoCard key={index}>
                  <PrismicNextImage
                    field={item.logo}
                    className="h-auto max-h-[48px] w-auto max-w-[85%] object-contain md:max-h-[64px]"
                    fallbackAlt=""
                  />
                </LogoCard>
              ))}
            </div>
          </LabeledGroup>
        )}

        {certificationLogos.length > 0 && (
          <LabeledGroup label={certifications_label}>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
              {certificationLogos.map((item, index) => (
                <LogoCard key={index}>
                  <PrismicNextImage
                    field={item.logo}
                    className="h-auto max-h-[72px] w-auto max-w-[85%] object-contain md:max-h-[88px]"
                    fallbackAlt=""
                  />
                </LogoCard>
              ))}
            </div>
          </LabeledGroup>
        )}

        {recognitionLogos.length > 0 && (
          <LabeledGroup label={recognitions_label}>
            <RecognitionStrip>
              {/* Row marquee behavior reused from IndustryTestimonialsCarousel
                  (withCertifications). The recognition row keeps its own logo
                  styling; only the animation + grayscale→color hover are
                  shared. */}
              <DynamicMarquee
                speed={45}
                pauseOnHover
                autoFill
                gradient
                gradientColor="#FFFFFF"
                gradientWidth={80}
              >
                {recognitionLogos.map((item, index) => {
                  // Accept whatever shape Prismic returns for the color asset
                  // (Link-to-Media or Image) and pull a usable URL.
                  const colorRaw = (item as { logo_color?: unknown })
                    .logo_color;
                  const colorUrl =
                    colorRaw &&
                    typeof colorRaw === 'object' &&
                    'url' in colorRaw &&
                    typeof (colorRaw as { url?: unknown }).url === 'string' &&
                    (colorRaw as { url: string }).url.length > 0
                      ? (colorRaw as { url: string }).url
                      : undefined;

                  return (
                    <div
                      key={index}
                      className="group/logo relative mx-8 flex items-center justify-center md:mx-10"
                    >
                      {/* Default (grayscale) logo */}
                      <PrismicNextImage
                        field={item.logo}
                        className={`h-auto max-h-[72px] w-auto max-w-none object-contain grayscale opacity-70 transition-all duration-300 ${
                          colorUrl
                            ? 'group-hover/logo:opacity-0'
                            : 'group-hover/logo:opacity-100 group-hover/logo:grayscale-0'
                        }`}
                        fallbackAlt=""
                      />
                      {/* Color asset revealed on hover (only if uploaded).
                          Rendered as a CSS background so Prismic SVGs render
                          reliably. */}
                      {colorUrl && (
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-0 bg-contain bg-center bg-no-repeat opacity-0 transition-opacity duration-300 group-hover/logo:opacity-100"
                          style={{ backgroundImage: `url("${colorUrl}")` }}
                        />
                      )}
                    </div>
                  );
                })}
              </DynamicMarquee>
            </RecognitionStrip>
          </LabeledGroup>
        )}
      </RevealOnScroll>
    </section>
  );
};

export default PartnersCertificationsRecognitions;
